import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { axiosInstance } from "@/lib/api";
import type {
  AssignableRole,
  BrandRef,
  IncomingOrganizationInvite,
  OrganizationInviteSummary,
  OrganizationMember,
  OrganizationRole,
} from "@/types/organization.type";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import { useAuthStore } from "@/stores/authStore";
import type { User } from "@/types/user.type";
import { queue } from "@/components/Toast";
import { brandGuestsKey, type BrandGuests } from "@/hooks/useBrandGuests";

function errorMessage(err: unknown, fallback: string): string {
  if (isAxiosError(err)) {
    return (err.response?.data as { message?: string })?.message ?? err.message ?? fallback;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

export { errorMessage as organizationErrorMessage };

export function useSwitchOrganization() {
  const { setSelectedOrganizationId, setSelectedBrandId } = useWorkspaceStore();

  return (organizationId: string) => {
    setSelectedOrganizationId(organizationId);
    setSelectedBrandId(null);
  };
}

export function useRenameOrganization(organizationId: string | null) {
  const queryClient = useQueryClient();
  const { user, setUser } = useAuthStore();

  return useMutation({
    mutationFn: async (name: string) => {
      const res = await axiosInstance.patch<{ id: string; name: string }>(
        `/organization/${organizationId}`,
        { name },
      );
      return res.data;
    },
    // Optimistic: the new name shows everywhere at once; the old one comes back on failure.
    onMutate: (name) => {
      if (!user || !organizationId) return { previous: null };
      const previous =
        user.userOrganizations.find((uo) => uo.organization.id === organizationId)?.organization.name ?? null;
      const updatedUser: User = {
        ...user,
        userOrganizations: user.userOrganizations.map((uo) =>
          uo.organization.id === organizationId ? { ...uo, organization: { ...uo.organization, name } } : uo,
        ),
      };
      setUser(updatedUser);
      queryClient.setQueryData(["currentUser", user.id], updatedUser);
      return { previous };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brands", organizationId] });
      queue.add({
        title: "Workspace renamed",
        variant: "success",
      });
    },
    onError: (_err, _name, context) => {
      const current = useAuthStore.getState().user;
      if (current && context?.previous) {
        const restored: User = {
          ...current,
          userOrganizations: current.userOrganizations.map((uo) =>
            uo.organization.id === organizationId
              ? { ...uo, organization: { ...uo.organization, name: context.previous as string } }
              : uo,
          ),
        };
        setUser(restored);
        queryClient.setQueryData(["currentUser", current.id], restored);
      }
      queue.add({
        title: "Could not rename workspace",
        variant: "error",
      });
    },
  });
}

export function useOrganizationMembers(organizationId: string | null) {
  return useQuery({
    queryKey: ["organizationMembers", organizationId],
    queryFn: async () => {
      const res = await axiosInstance.get<{ members: OrganizationMember[] }>(
        `/organization/${organizationId}/members`,
      );
      return res.data.members;
    },
    enabled: !!organizationId,
  });
}

export function useOrganizationInvites(organizationId: string | null, isOwner: boolean) {
  return useQuery({
    queryKey: ["organizationInvites", organizationId],
    queryFn: async () => {
      const res = await axiosInstance.get<{ invites: OrganizationInviteSummary[] }>(
        `/organization/${organizationId}/invites`,
      );
      return res.data.invites;
    },
    enabled: !!organizationId && isOwner,
  });
}

export function useMyInvites() {
  return useQuery({
    queryKey: ["myInvites"],
    queryFn: async () => {
      const res = await axiosInstance.get<{ invites: IncomingOrganizationInvite[] }>(
        "/organization/invites",
      );
      return res.data.invites;
    },
  });
}

export type SendInvitePayload = {
  email: string;
  role: AssignableRole;
  /** Guest only: the brands the guest is scoped to (names shown optimistically). */
  brands?: BrandRef[];
};

/** What POST /organization/invite answers. A known guest is granted straight away. */
type SendInviteResponse = {
  message: string;
  invite: { id: string; email: string; role: OrganizationRole; brandIds?: string[]; expiresAt: string } | null;
  emailSent: boolean;
  granted: boolean;
};

const invitesKey = (organizationId: string | null) => ["organizationInvites", organizationId];
const membersKey = (organizationId: string | null) => ["organizationMembers", organizationId];

export function useSendInvite(organizationId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: SendInvitePayload) => {
      const res = await axiosInstance.post<SendInviteResponse>("/organization/invite", {
        email: payload.email,
        organizationId,
        role: payload.role,
        ...(payload.role === "Guest" ? { brandIds: (payload.brands ?? []).map((b) => b.id) } : {}),
      });
      return res.data;
    },
    onMutate: async (payload) => {
      // The pending row shows at once. A re-invite of a pending guest widens that
      // row server-side, so merge by email rather than add a duplicate.
      await queryClient.cancelQueries({ queryKey: invitesKey(organizationId) });
      const email = payload.email.trim().toLowerCase();
      const tempId = `pending-${email}`;
      queryClient.setQueryData<OrganizationInviteSummary[]>(invitesKey(organizationId), (old) => {
        const list = old ?? [];
        const existing = list.find((i) => i.email.toLowerCase() === email && i.role === payload.role);
        if (existing && payload.role === "Guest") {
          const merged = new Map((existing.brands ?? []).map((b) => [b.id, b]));
          for (const brand of payload.brands ?? []) merged.set(brand.id, brand);
          return list.map((i) => (i === existing ? { ...i, brands: [...merged.values()] } : i));
        }
        const optimistic: OrganizationInviteSummary = {
          id: tempId,
          email,
          role: payload.role,
          brands: payload.role === "Guest" ? payload.brands : undefined,
          inviter: {},
          createdAt: new Date().toISOString(),
          expiresAt: new Date(Date.now() + 7 * 86_400_000).toISOString(),
        };
        return [optimistic, ...list];
      });
      // A client invite also shows at once on each picked brand's Clients page.
      const brandIds = payload.role === "Guest" ? (payload.brands ?? []).map((b) => b.id) : [];
      for (const brandId of brandIds) {
        await queryClient.cancelQueries({ queryKey: brandGuestsKey(brandId) });
        queryClient.setQueryData<BrandGuests>(brandGuestsKey(brandId), (prev) =>
          prev
            ? {
                ...prev,
                invites: [
                  ...prev.invites.filter((i) => i.email.toLowerCase() !== email),
                  {
                    id: `optimistic:${email}`,
                    email,
                    invitedBy: "",
                    createdAt: new Date().toISOString(),
                    expiresAt: new Date(Date.now() + 7 * 86_400_000).toISOString(),
                  },
                ],
              }
            : prev,
        );
      }
      return { tempId, email, brandIds };
    },
    onSuccess: (data) => {
      queue.add({
        title: data.granted ? "Access granted" : "Invite sent",
        description: data.granted
          ? "They already had an account here, so they can open those brands now."
          : "The invitation has been created.",
        variant: "success",
      });
    },
    onError: (err, _payload, context) => {
      if (context) {
        queryClient.setQueryData<OrganizationInviteSummary[]>(invitesKey(organizationId), (old) =>
          (old ?? []).filter((i) => i.id !== context.tempId),
        );
        for (const brandId of context.brandIds) {
          queryClient.setQueryData<BrandGuests>(brandGuestsKey(brandId), (prev) =>
            prev ? { ...prev, invites: prev.invites.filter((i) => i.id !== `optimistic:${context.email}`) } : prev,
          );
        }
      }
      queue.add({
        title: "Could not send invite",
        description: errorMessage(err, "Please check the email and try again."),
        variant: "error",
      });
    },
    onSettled: (data, _err, payload) => {
      queryClient.invalidateQueries({ queryKey: invitesKey(organizationId) });
      // A direct grant to an existing guest changes the member list instead.
      if (data?.granted) queryClient.invalidateQueries({ queryKey: membersKey(organizationId) });
      // Lives in the hook, not the caller: the dialog has closed by now.
      for (const brand of payload.role === "Guest" ? (payload.brands ?? []) : []) {
        queryClient.invalidateQueries({ queryKey: brandGuestsKey(brand.id) });
      }
    },
  });
}

export function useAcceptInvite() {
  const queryClient = useQueryClient();
  const { setUser } = useAuthStore();
  const switchOrganization = useSwitchOrganization();

  return useMutation({
    mutationFn: async (inviteId: string) => {
      const res = await axiosInstance.post<{
        message: string;
        organizationId: string;
        organization: { id: string; name: string };
        role: OrganizationRole;
      }>(`/organization/invite/${inviteId}/accept`);
      return res.data;
    },
    onMutate: async (inviteId) => {
      await queryClient.cancelQueries({ queryKey: ["myInvites"] });
      const previous = queryClient.getQueryData<IncomingOrganizationInvite[]>(["myInvites"]);
      queryClient.setQueryData<IncomingOrganizationInvite[]>(["myInvites"], (old) =>
        (old ?? []).filter((i) => i.id !== inviteId),
      );
      return { removed: previous?.find((i) => i.id === inviteId) ?? null };
    },
    onSuccess: async (data) => {
      const res = await axiosInstance.get<{ user: User }>("/auth/user-info");
      setUser(res.data.user);
      queryClient.setQueryData(["currentUser", res.data.user.id], res.data.user);
      queryClient.invalidateQueries({ queryKey: ["myInvites"] });
      switchOrganization(data.organizationId);
      queue.add({
        title: "Invitation accepted",
        description: `You joined ${data.organization.name}.`,
        variant: "success",
      });
    },
    onError: (err, _inviteId, context) => {
      if (context?.removed) {
        const removed = context.removed;
        queryClient.setQueryData<IncomingOrganizationInvite[]>(["myInvites"], (old) =>
          old?.some((i) => i.id === removed.id) ? old : [removed, ...(old ?? [])],
        );
      }
      queue.add({
        title: "Could not accept invite",
        description: errorMessage(err, "Please try again."),
        variant: "error",
      });
    },
  });
}

export function useDeclineInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (inviteId: string) => {
      const res = await axiosInstance.post(`/organization/invite/${inviteId}/decline`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["myInvites"] });
      queue.add({
        title: "Invitation declined",
        variant: "success",
      });
    },
    onError: () => {
      queue.add({
        title: "Could not decline invite",
        variant: "error",
      });
    },
  });
}

export function useRevokeInvite(organizationId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (inviteId: string) => {
      const res = await axiosInstance.post(`/organization/invite/${inviteId}/revoke`);
      return res.data;
    },
    onMutate: async (inviteId) => {
      await queryClient.cancelQueries({ queryKey: invitesKey(organizationId) });
      const list = queryClient.getQueryData<OrganizationInviteSummary[]>(invitesKey(organizationId));
      const index = list?.findIndex((i) => i.id === inviteId) ?? -1;
      const removed = index >= 0 ? list![index] : null;
      queryClient.setQueryData<OrganizationInviteSummary[]>(invitesKey(organizationId), (old) =>
        (old ?? []).filter((i) => i.id !== inviteId),
      );
      return { removed, index };
    },
    onSuccess: () => {
      queue.add({
        title: "Invite revoked",
        variant: "success",
      });
    },
    onError: (_err, _inviteId, context) => {
      if (context?.removed) {
        const { removed, index } = context;
        queryClient.setQueryData<OrganizationInviteSummary[]>(invitesKey(organizationId), (old) => {
          const list = [...(old ?? [])];
          if (list.some((i) => i.id === removed.id)) return list;
          list.splice(Math.max(0, index), 0, removed);
          return list;
        });
      }
      queue.add({
        title: "Could not revoke invite",
        variant: "error",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: invitesKey(organizationId) });
    },
  });
}

export type UpdateMemberRolePayload = {
  userId: string;
  role: AssignableRole;
  /** Guest only: the brands to scope them to (replaces a guest's current set). */
  brands?: BrandRef[];
  /** The caller shows its own toast (Settings → People's Undo). */
  quiet?: boolean;
};

export function useUpdateMemberRole(organizationId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdateMemberRolePayload) => {
      const res = await axiosInstance.patch(
        `/organization/${organizationId}/members/${payload.userId}`,
        payload.role === "Guest"
          ? { role: payload.role, brandIds: (payload.brands ?? []).map((b) => b.id) }
          : { role: payload.role },
      );
      return res.data;
    },
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: membersKey(organizationId) });
      const previous =
        queryClient
          .getQueryData<OrganizationMember[]>(membersKey(organizationId))
          ?.find((m) => m.userId === payload.userId) ?? null;
      queryClient.setQueryData<OrganizationMember[]>(membersKey(organizationId), (old) =>
        (old ?? []).map((m) =>
          m.userId === payload.userId
            ? { ...m, role: payload.role, brands: payload.role === "Guest" ? (payload.brands ?? []) : null }
            : m,
        ),
      );
      return { previous };
    },
    onSuccess: (_data, payload) => {
      if (payload.quiet) return;
      queue.add({
        title: payload.role === "Guest" ? "Client access updated" : "Role updated",
        variant: "success",
      });
    },
    onError: (err, payload, context) => {
      // Only this member goes back: another row may have changed meanwhile.
      if (context?.previous) {
        const previous = context.previous;
        queryClient.setQueryData<OrganizationMember[]>(membersKey(organizationId), (old) =>
          (old ?? []).map((m) => (m.userId === payload.userId ? previous : m)),
        );
      }
      queue.add({
        title: "Could not update role",
        description: errorMessage(err, "Please try again."),
        variant: "error",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: membersKey(organizationId) });
      // A client's brands changed: every brand's Clients list follows.
      queryClient.invalidateQueries({ queryKey: ["brandGuests"] });
    },
  });
}

export function useRemoveMember(organizationId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const res = await axiosInstance.delete(
        `/organization/${organizationId}/members/${userId}`,
      );
      return res.data;
    },
    onMutate: async (userId) => {
      await queryClient.cancelQueries({ queryKey: membersKey(organizationId) });
      const list = queryClient.getQueryData<OrganizationMember[]>(membersKey(organizationId));
      const index = list?.findIndex((m) => m.userId === userId) ?? -1;
      const removed = index >= 0 ? list![index] : null;
      queryClient.setQueryData<OrganizationMember[]>(membersKey(organizationId), (old) =>
        (old ?? []).filter((m) => m.userId !== userId),
      );
      return { removed, index };
    },
    onSuccess: () => {
      queue.add({
        title: "Member removed",
        variant: "success",
      });
    },
    onError: (err, _userId, context) => {
      if (context?.removed) {
        const { removed, index } = context;
        queryClient.setQueryData<OrganizationMember[]>(membersKey(organizationId), (old) => {
          const list = [...(old ?? [])];
          if (list.some((m) => m.userId === removed.userId)) return list;
          list.splice(Math.max(0, index), 0, removed);
          return list;
        });
      }
      queue.add({
        title: "Could not remove member",
        description: errorMessage(err, "Please try again."),
        variant: "error",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: membersKey(organizationId) });
    },
  });
}
